import React from 'react';
import { Box, TableRow, TableCell, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BoyIcon from '@mui/icons-material/Boy';
import { getCapturedImageDisplayUrl } from '../../../services/s3/captureUrlService';
import PersonAvatar from '../../events/shared/PersonAvatar';

/**
 * UnknownPromotionEventRow - Renders unknown face promotion to known person event
 */
const UnknownPromotionEventRow = React.memo(function UnknownPromotionEventRow({
  event,
  isChild = false,
  address,
  onLocationClick
}) {
  console.log('[UnknownPromotionEventRow] Rendering unknown_promotion event:', event.eventId, event);

  const hasValidMetadata = event.metadata &&
    typeof event.metadata === 'object' &&
    !event.metadata.S &&
    !event.metadata.M &&
    !event.metadata.NULL;

  if (!hasValidMetadata) {
    console.log('[UnknownPromotionEventRow] Invalid metadata, skipping render:', event.metadata);
    return null;
  }

  const capturedImageUrl = getCapturedImageDisplayUrl(event.metadata.capturedImageUrl);
  const croppedFaceUrl = getCapturedImageDisplayUrl(event.metadata.croppedFaceUrl);
  const timestamp = new Date(event.timestamp || event.createdAt);
  const personId = event.metadata.personId || 'Unknown';
  const firstName = event.metadata.firstName || '';
  const lastName = event.metadata.lastName || '';
  const group = event.metadata.group || '';
  const dataset = event.metadata.dataset || '';

  const personName = `${firstName} ${lastName}`.trim() || personId;

  // Determine background color
  const backgroundColor = isChild ? '#f5f5f5' : '#fff';
  const borderColor = isChild ? '4px solid #4caf50' : 'none';

  return (
    <TableRow
      hover
      sx={{
        backgroundColor,
        borderLeft: borderColor,
        '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.4)' },
        '&:hover': {
          backgroundColor,
          filter: 'brightness(0.98)'
        }
      }}
    >
      {/* Event Type Icon */}
      <TableCell align="center" sx={{ width: 40 }}>
        <Tooltip title="Unknown Promoted to Known">
          <PersonAddIcon sx={{ color: 'success.main', fontSize: 28 }} />
        </Tooltip>
      </TableCell>

      {/* Expand/Collapse Column (empty) */}
      <TableCell align="center" sx={{ width: 40 }} />

      {/* Event Details */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Manual promotion indicator */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.25,
            flexShrink: 0
          }}>
            {/* Manual icon */}
            <BoyIcon sx={{ color: 'success.main', fontSize: 20 }} />

            {/* Manual indicator circle */}
            <Box sx={{
              position: 'relative',
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'conic-gradient(#2e7d32 360deg, rgba(0, 0, 0, 0.1) 0deg)'
            }}>
              <Box sx={{
                width: 23,
                height: 23,
                borderRadius: '50%',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    color: '#2e7d32',
                    lineHeight: 1
                  }}
                >
                  M
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Person face thumbnail */}
          {(croppedFaceUrl || capturedImageUrl) && (
            <Box sx={{
              border: '3px solid',
              borderColor: 'success.main',
              borderRadius: '50%',
              padding: '2px',
              display: 'inline-flex',
              flexShrink: 0,
              '& .MuiAvatar-root': { mr: 0 }
            }}>
              <PersonAvatar
                imageUrl={croppedFaceUrl || capturedImageUrl}
                name={personName}
                size={60}
              />
            </Box>
          )}

          {/* Event info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
              {personName}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Promoted to known person
            </Typography>
          </Box>
        </Box>
      </TableCell>

      {/* Time */}
      <TableCell sx={{ whiteSpace: 'nowrap', width: 100 }}>
        <Typography variant="caption" color="text.secondary">
          {timestamp.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </TableCell>

      {/* User Account */}
      <TableCell sx={{ width: 120 }}>
        <Typography variant="caption" color="text.secondary">
          {event.userHandle || event.userEmail || event.userId || '—'}
        </Typography>
      </TableCell>

      {/* Location - Manual promotions don't have physical location */}
      <TableCell sx={{ width: 200 }}>
      </TableCell>
    </TableRow>
  );
});

export default UnknownPromotionEventRow;
